
"use client";

import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
  animate,
  useReducedMotion,
} from "framer-motion";
import {
  UserCircle,
  MessageSquare,
  BookOpen,
  Cpu,
  Briefcase,
  Code2,
  ShieldCheck,
 
  LucideIcon,
} from "lucide-react";
import React, { useEffect, useState, useRef } from "react";

/* ============================= */
/* BORDER BEAM                  */
/* ============================= */

const BorderBeam = ({
  isHovered,
  radius = 40,
}: {
  isHovered: boolean;
  radius?: number;
}) => {
  const pathRef = useRef<SVGRectElement>(null);
  const [length, setLength] = useState(0);

  useEffect(() => {
    if (pathRef.current) {
      setLength(pathRef.current.getTotalLength());
    }
  }, []);

  return (
    <svg className="absolute inset-0 pointer-events-none" width="100%" height="100%">
      <defs>
        <linearGradient id="beam-gradient" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="transparent" />
          <stop offset="45%" stopColor="rgba(59,130,246,0.9)" />
          <stop offset="55%" stopColor="rgba(59,130,246,0.9)" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>

        <filter id="beam-glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <motion.rect
        ref={pathRef}
        x="1"
        y="1"
        width="calc(100% - 2px)"
        height="calc(100% - 2px)"
        rx={radius}
        fill="none"
        stroke="url(#beam-gradient)"
        strokeWidth="1.5"
        filter="url(#beam-glow)"
        strokeDasharray={length ? `${length * 0.2} ${length}` : "0 0"}
        animate={{
          strokeDashoffset: [0, -length],
          opacity: isHovered ? 0.9 : 0.4,
        }}
        transition={{
          repeat: Infinity,
          duration: isHovered ? 2 : 4,
          ease: "linear",
        }}
      />
    </svg>
  );
};

/* ============================= */
/* CARD                         */
/* ============================= */

interface LayerProps {
  icon: LucideIcon;
  title: string;
  items: string[];
  description: string;
  index: number;
}

const AutoplayCard = ({
  icon: Icon,
  title,
  items,
  description,
  index,
}: LayerProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  /* -------- Detect Mobile / Touch -------- */

  const shouldReduceMotion = useReducedMotion();
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsTouchDevice(window.matchMedia("(hover: none)").matches);
    }
  }, []);
  
  const disableMotion = shouldReduceMotion || isTouchDevice;

  /* -------- Motion Values -------- */

  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);
  const spotlightX = useMotionValue(0);
  const spotlightY = useMotionValue(0);

  const rotateX = useSpring(
    useTransform(y, [0, 1], disableMotion ? [0, 0] : [10, -10]),
    { stiffness: 60, damping: 20 }
  );
  
  const rotateY = useSpring(
    useTransform(x, [0, 1], disableMotion ? [0, 0] : [-10, 10]),
    { stiffness: 60, damping: 20 }
  );
  const spotlightBackground = useMotionTemplate`
  radial-gradient(
    220px circle at ${spotlightX}px ${spotlightY}px,
    rgba(59,130,246,${isHovered ? 0.12 : 0.04}),
    transparent 80%
  )
`;

  /* -------- Autoplay Animation (Desktop Only) -------- */

  useEffect(() => {
    if (!isHovered && !disableMotion) {
      const controlsX = animate(x, [0.4, 0.6, 0.4], {
        duration: 5,
        repeat: Infinity,
        ease: "easeInOut",
      });

      const controlsY = animate(y, [0.4, 0.4, 0.6, 0.4], {
        duration: 7,
        repeat: Infinity,
        ease: "easeInOut",
      });

      return () => {
        controlsX.stop();
        controlsY.stop();
      };
    }
  }, [isHovered, disableMotion]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disableMotion) return;

    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();

    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;

    x.set(px);
    y.set(py);
    spotlightX.set(e.clientX - rect.left);
    spotlightY.set(e.clientY - rect.top);
  };

  return (
    
    <motion.div
      ref={cardRef}
      onMouseMove={!disableMotion ? handleMouseMove : undefined}
      onMouseLeave={!disableMotion ? () => setIsHovered(false) : undefined}
      style={
        !disableMotion
          ? { rotateX, rotateY, transformStyle: "preserve-3d" }
          : undefined
      }
      className="group relative p-8 rounded-[2.5rem]
      border border-slate-200
      bg-white/70 backdrop-blur-sm
      overflow-hidden transition-all duration-700
      hover:border-blue-400/40
      hover:shadow-[0_20px_60px_rgba(59,130,246,0.08)]"
    >
      {/* Border Beam Desktop Only */}
      {!disableMotion && <BorderBeam isHovered={isHovered} radius={40} />}

      {/* Spotlight Desktop Only */}
      {!disableMotion && (
        <motion.div
          className="pointer-events-none absolute -inset-px opacity-40 group-hover:opacity-80 transition-opacity duration-500"
          style={{ background: spotlightBackground }}
        />
      )}

      <div className="relative z-10 flex flex-col h-full">
        <div
          className={`w-14 h-14 rounded-2xl bg-blue-50 border border-slate-400
          flex items-center justify-center mb-8 transition-all duration-500
          ${
            isHovered && !disableMotion
              ? "text-blue-600 border-blue-300 scale-110"
              : "text-slate-500"
          }`}
        >
          <Icon size={24} strokeWidth={1.5} />
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-mono text-blue-600/70 font-bold uppercase tracking-wider">
            System_L{index + 1}
          </span>
          <h3 className="text-2xl font-bold tracking-tight text-[#1A1A1A]">
            {title}
          </h3>
        </div>

        <p className="text-sm text-slate-600 mb-8 leading-relaxed font-light">
          {description}
        </p>

        <div className="flex flex-wrap gap-2 mt-auto">
          {items.map((item, i) => (
            <span
              key={i}
              className="text-[9px] font-mono uppercase tracking-widest
              text-slate-500 px-2 py-1 rounded border border-slate-200 bg-white/50"
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

/* ============================= */
/* SECTION                      */
/* ============================= */

const ArchitectureSection = () => {
    const layers = [
        {
          icon: UserCircle,
          title: "Identity",
          description:
            "Domain-based login system serving as the root namespace for all operations.",
          items: ["Domain Login", "RBAC", "Team Management", "Session Control", "Audit Logs"],
        },
        {
          icon: MessageSquare,
          title: "Communication Layer",
          description:
            "Unified communication protocol for mail, chat, and collaborative scheduling.",
          items: ["Chat", "Mail", "Calendar", "Video Meeting", "File Sharing"],
        },
        {
          icon: BookOpen,
          title: "Knowledge",
          description:
            "Permission-aware search and documentation layer with version control.",
          items: ["Docs/Wiki", "File Abstraction", "Search", "Version Control", "Permission-aware Search"],
        },
        {
          icon: Cpu,
          title: "AI & Automation",
          description:
            "Agent-driven workflow engine operating within domain security boundaries.",
          items: ["Agent Studio", "Workflow", "Approvals", "AI Assistants", "Tool"],
        },
        {
          icon: Briefcase,
          title: "Business Ops",
          description:
            "A comprehensive console for payments, analytics, and merchant governance.",
          items: ["Payments", "Loyalty", "Analytics", "Affiliate", "Multi-location roles"],
        },
        {
          icon: Code2,
          title: "Developer",
          description:
            "Extensible API gateway and SDKs for custom app building.",
          items: ["APIs", "Webhooks", "SDKs"],
        },
      ];
  return (
    <section id="architecture" className="relative py-32 bg-[#F8F9FF] text-[#1A1A1A]">
      <div className="container mx-auto px-6">
      <div className="max-w-4xl mb-24">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="inline-block rounded-full px-4 py-1.5 font-mono border border-indigo-100 bg-indigo-50 text-[10px] font-bold tracking-[0.3em] uppercase mb-4 text-blue-600"
          >
            Architecture 
          </motion.div>

          <h2 className="text-5xl md:text-7xl tracking-tighter mb-8 leading-tight text-slate-900">
          Architecture Stack
          </h2>

          <p className="text-slate-600 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
            Gao Workspace unifies <span className="text-blue-600 font-medium">Identity, Payment, and AI</span> under a single domain root.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {layers.map((layer, index) => (
            <AutoplayCard key={index} {...layer} index={index} />
          ))}
          <motion.div
  initial={{ opacity: 0, scale: 0.98 }}
  whileInView={{ opacity: 1, scale: 1 }}
  whileHover={{ scale: 1.01 }}
  transition={{ duration: 0.4 }}
  className="lg:col-span-3 group relative p-12 rounded-[3rem]
  border border-slate-200
  bg-white/80 backdrop-blur-sm
  overflow-hidden transition-all duration-700
  hover:border-blue-400/40
  hover:shadow-[0_30px_80px_rgba(59,130,246,0.08)]"
>
  {/* ===== Stripe-Level Border Beam ===== */}
  <BorderBeam isHovered={true} radius={48} />

  {/* Background Glow */}
  <div className="absolute -top-32 -right-32 w-[500px] h-[500px] 
  bg-blue-500/10 blur-[160px] opacity-40 group-hover:opacity-70 
  transition-opacity duration-700" />

  <div className="relative z-10 flex flex-col lg:flex-row items-center gap-10">
    
    {/* Icon */}
    <div className="w-20 h-20 rounded-3xl 
    bg-blue-50 border border-blue-200
    text-blue-600
    flex items-center justify-center
    transition-all duration-500
    group-hover:scale-110
    group-hover:shadow-[0_0_40px_rgba(59,130,246,0.25)]">
      <ShieldCheck size={40} strokeWidth={1.5} />
    </div>

    {/* Text */}
    <div className="flex-1 text-center lg:text-left">
      <h3 className="text-3xl font-bold mb-2 text-[#1A1A1A]">
        Security & Governance
      </h3>
      <p className="text-slate-600 max-w-xl font-light group-hover:text-slate-800 transition-colors">
        Advanced passkey authentication and hierarchical governance.
      </p>
    </div>

    {/* Tags */}
    <div className="flex flex-wrap justify-center gap-3">
      {["2FA", "RBAC", "Audits", "Retention"].map((t) => (
        <span
          key={t}
          className="px-4 py-2 rounded-xl
          bg-white/60
          border border-slate-200
          text-[10px] font-mono
          text-slate-600
          uppercase tracking-widest
          transition-all
          group-hover:border-blue-200
          group-hover:text-blue-600"
        >
          {t}
        </span>
      ))}
    </div>
  </div>
 </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ArchitectureSection;
